/**
 * /admin/reels/secret-share — burnable Mux secret shares.
 * Loads private-reels resolved IDs for snapshot picker; refresh after upload.
 */
import { useCallback, useEffect, useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { SecretShareMuxSection } from "@/components/admin/SecretShareMuxSection";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function PrivateReelsSecretSharePage() {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [feedIds, setFeedIds] = useState<string[]>([]);

  const loadFeed = useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (!opts?.quiet) setLoading(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch("/api/admin/private-reels", { headers });
        const data = (await res.json()) as {
          ok?: boolean;
          resolvedPlaybackIds?: string[];
          error?: string;
        };
        if (!res.ok || !data.ok) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        setFeedIds(data.resolvedPlaybackIds || []);
      } catch (e) {
        toast({
          title: "Error al cargar feed",
          description: e instanceof Error ? e.message : "Error",
          variant: "destructive",
        });
      } finally {
        if (!opts?.quiet) setLoading(false);
      }
    },
    [getAuthHeaders, toast],
  );

  useEffect(() => {
    void loadFeed();
  }, [loadFeed]);

  if (loading && feedIds.length === 0) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando feed para snapshot…
      </div>
    );
  }

  return (
    <SecretShareMuxSection
      feedPlaybackIds={feedIds}
      onFeedRefresh={() => loadFeed({ quiet: true })}
    />
  );
}
